"use client";

import Box from "@mui/material/Box";
import { alpha, keyframes, styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/pro-regular-svg-icons/faPhone";
import { DreamMarkdownReader } from "@portal/app/_components/ui-elements/DreamMarkdown/DreamMarkdownReader";
import { useFormFunnelHeadingLevel } from "../../FormFunnelHeadingContext";

interface QueueHeadlineProps {
  title: string;
  /** Plain text (rendered as a paragraph) or markdown when isMarkdown is true. */
  body: string;
  isMarkdown?: boolean;
}

export const QueueHeadline = ({ title, body, isMarkdown = false }: QueueHeadlineProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  return (
    <>
      <TitleGroup>
        <PhoneIconCircle aria-hidden="true" data-testid="success-phone-icon">
          <FontAwesomeIcon icon={faPhone} />
        </PhoneIconCircle>
        <Typography
          variant="h4"
          component={headingLevel}
          sx={{ fontWeight: 700, color: "primary.main" }}
        >
          {title}
        </Typography>
      </TitleGroup>
      {isMarkdown ? (
        <StyledMarkdown value={body} data-testid="success-page-message" />
      ) : (
        <Typography variant="body1" data-testid="success-page-message">
          {body}
        </Typography>
      )}
    </>
  );
};

const ring = keyframes`
  0%, 100% {
    transform: rotate(0deg);
  }
  10%, 30%, 50% {
    transform: rotate(-12deg);
  }
  20%, 40%, 60% {
    transform: rotate(12deg);
  }
  70% {
    transform: rotate(0deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.18);
    opacity: 0;
  }
`;

const TitleGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const PhoneIconCircle = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: "50%",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  fontSize: 22,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -6,
    borderRadius: "50%",
    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    animation: `${pulse} 1.8s ease-out infinite`,
  },
  "& svg": {
    animation: `${ring} 1.8s ease-in-out infinite`,
    transformOrigin: "50% 50%",
  },
}));

const StyledMarkdown = styled(DreamMarkdownReader)(({ theme }) => ({
  ...theme.typography.body1,
  textAlign: "center",
}));
